import { ButtonERP } from "@finanedi.es/storybook"
import {
  faArrowLeft,
  faArrowsRotateReverse,
  faFileExport,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GenerarFicheroModelo347CommandProps } from "api/Commands/GenerarFicheroModelo347Command"
import { postDeclaracionModelo347 } from "api/DeclaracionModelo"
import { useDeclaracion347 } from "api/DeclaracionModelo.hook"
import { MODELO_IMPUESTO } from "api/Types/ModeloImpuesto"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import Spinner from "common/components/Spinner"
import notification from "common/utils/notification"
import { TFunction } from "i18next"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useHistory, useLocation } from "react-router-dom"
import * as Yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import EjercicioDeclaracion from "../../EjercicioDeclaracion"
import { ModalPresentarModelo } from "../../ModalPresentarModelo"
import { PlantillaDeclaracion } from "../../PlantillaDeclaracion"
import PlantillaDeclaracionEstandar from "../../PlantillaDeclaracionEstandar"
import {
  Modelo347FormModel,
  postDeclaracionModelo347Adapter,
} from "../postDeclaracionModelo347.adapter"
import Declarados347 from "./Declarados347/Declarados347"
import Inmuebles347 from "./Inmueble347/Inmueble347"

const SECCIONES_TYPES = {
  DECLARANTE: 1,
  EJERCICIO: 2,
  RESUMEN_DATOS: 3,
  DECLARADOS: 4,
  INMUEBLES: 5,
}
const SECCIONES = (t: TFunction) => [
  {
    id: SECCIONES_TYPES.DECLARANTE,
    label: `${SECCIONES_TYPES.DECLARANTE} - ${t(
      "taxSummary.declaracion.declarante"
    )}`,
  },
  {
    id: SECCIONES_TYPES.EJERCICIO,
    label: `${SECCIONES_TYPES.EJERCICIO} - ${t(
      "taxSummary.declaracion.ejercicio"
    )}`,
  },
  {
    id: SECCIONES_TYPES.RESUMEN_DATOS,
    label: `${SECCIONES_TYPES.RESUMEN_DATOS} - ${t(
      "taxSummary.declaracion.summary"
    )}`,
  },
  {
    id: SECCIONES_TYPES.DECLARADOS,
    label: `${SECCIONES_TYPES.DECLARADOS} - ${t(
      "declaracionModelo347.declarados.title"
    )}`,
  },
  {
    id: SECCIONES_TYPES.INMUEBLES,
    label: `${SECCIONES_TYPES.INMUEBLES} - ${t(
      "declaracionModelo347.inmuebles"
    )}`,
  },
]

const schemaModelo347 = Yup.object({
  nifDeclarante: Yup.string().required(),
  telefonoPersona: Yup.string().required(),
  nombreDeclarante: Yup.string().required(),
  nifRepresentanteLegal: Yup.string().required(),
  apellidosYNombrePersona: Yup.string().required(),
  declarados: Yup.array().of(
    Yup.object({
      denominacionDeclarado: Yup.string().required(),
    })
  ),
  inmuebles: Yup.array().of(
    Yup.object({
      denominacionArrendatario: Yup.string().required(),
      importeOperacion: Yup.number().required(),
      situacionInmueble: Yup.string().required(),
    })
  ),
}).required()

export type Modelo347Form = Yup.InferType<typeof schemaModelo347>

const DeclaracionModelo347 = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [tabSelected, setSelectedTab] = useState(SECCIONES_TYPES.DECLARANTE)
  const [showModalPresentar, setShowModalPresentar] = useState<boolean>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const {
    activeCompany: { razonSocial, nif },
  } = useUsuarioEmpresaActiva()
  const { state } = useLocation() as { state?: { year?: number } }

  const {
    declaracion347Data,
    declaracion347DataIsLoading,
    declaracion347DataRefetch,
  } = useDeclaracion347({
    queryParams: {
      Anyo: Number(state?.year),
    },
  })

  const defaultValues = {
    ejercicio: state?.year,
    nifDeclarante: nif,
    nombreDeclarante: razonSocial,
    "01": declaracion347Data?.casilla01,
    "02": declaracion347Data?.casilla02,
    "03": declaracion347Data?.casilla03,
    "04": declaracion347Data?.casilla04,
    declarados: declaracion347Data?.declarados,
  }
  const {
    setValue,
    watch,
    reset,
    formState: { errors },
    trigger,
  } = useForm<$TSMigrationFixMe>({
    values: defaultValues,
    resolver: yupResolver(schemaModelo347),
    mode: "onChange",
    reValidateMode: "onChange",
  })

  const values = watch()

  const handlePresentarClick = async () => {
    const isValid = await trigger()
    if (!isValid) {
      notification.warning({
        body: t("notification.warningModelDeclaration"),
      })
      const declaranteValid = await trigger([
        "nifDeclarante",
        "telefonoPersona",
        "nombreDeclarante",
        "nifRepresentanteLegal",
        "apellidosYNombrePersona",
      ])
      if (!declaranteValid) {
        setSelectedTab(SECCIONES_TYPES.DECLARANTE)
        return
      }

      const declaradosValid = await trigger(["declarados"])
      if (!declaradosValid) {
        setSelectedTab(SECCIONES_TYPES.DECLARADOS)
        return
      }
      const inmueblesValid = await trigger(["inmuebles"])
      if (!inmueblesValid) {
        setSelectedTab(SECCIONES_TYPES.INMUEBLES)
        return
      }
    }
    setShowModalPresentar(!showModalPresentar)
  }

  const handleUpdateClick = async () => {
    setIsUpdating(true)
    try {
      const { data: updatedData } = await declaracion347DataRefetch()
      if (updatedData) {
        reset(defaultValues)
        notification.success({
          body: `${t("taxSummary.declaracion.successNotification")}`,
        })
        setIsUpdating(false)
      }
    } catch (error) {
      setIsUpdating(false)
      notification.errorServidor()
    }
  }

  return (
    <div className='tw-w-full tw-h-full tw-overflow-hidden tw-bg-white tw-text-left'>
      <div className='tw-h-fit tw-p-3 tw-w-full tw-bg-white tw-shadow'>
        <div className='tw-h-11 tw-flex tw-flex-row tw-w-full tw-items-center tw-justify-between'>
          <div className='tw-flex tw-flex-row tw-gap-4 tw-items-center tw-h-full'>
            <ButtonERP
              customize={{ size: "md", type: "transparent" }}
              onClick={() => history.goBack()}
              icon={<FontAwesomeIcon icon={faArrowLeft} />}
            />
            <span className='tw-rounded tw-h-fit tw-py-1 tw-px-2 tw-text-sm tw-text-white tw-bg-[#E8AE0E]'>
              {MODELO_IMPUESTO.MODELO_347}
            </span>
            <span className='tw-text-base tw-font-medium tw-h-fit'>
              {t("declaracionModelo347.title")} -{" "}
              {state?.year ?? new Date().getFullYear()}
            </span>
          </div>
          <div className='tw-flex tw-flex-row tw-gap-3'>
            <ButtonERP
              customize={{ size: "md", type: "transparent" }}
              label={`${t("taxSummary.declaracion.updateButton")}`}
              loading={isUpdating}
              disabled={isUpdating}
              icon={<FontAwesomeIcon icon={faArrowsRotateReverse} />}
              onClick={() => {
                handleUpdateClick()
              }}
            />
            <ButtonERP
              customize={{ size: "md", type: "primary" }}
              onClick={handlePresentarClick}
              icon={<FontAwesomeIcon icon={faFileExport} />}
              label={`${t("taxSummary.declaracion.presentar")}`}
            />
          </div>
        </div>
      </div>

      <div className='tw-w-full tw-h-[calc(100%-68px)] tw-flex tw-flex-row'>
        <div className='tw-w-2/5 tw-p-6 tw-flex tw-flex-col tw-items-start tw-border-r tw-border-neutral-25'>
          <p className='tw-font-medium tw-text-base'>
            {t("taxSummary.declaracion.secciones")}
          </p>
          <div className='tw-flex tw-flex-col tw-items-start tw-mt-4 tw-w-full tw-gap-1 tw-text-left'>
            {SECCIONES(t).map(({ id, label }) => (
              <p
                key={id}
                className={`tw-h-10 tw-w-full tw-px-4 hover:tw-bg-neutral-25 ${
                  tabSelected === id ? "tw-bg-neutral-25" : ""
                } tw-flex tw-items-center tw-cursor-pointer tw-rounded tw-truncate`}
                onClick={() => setSelectedTab(id)}
              >
                {label}
              </p>
            ))}
          </div>
        </div>

        <div className='tw-w-4/5 tw-flex tw-flex-col tw-items-start tw-overflow-y-auto'>
          <div className='tw-w-full tw-text-base tw-font-medium tw-flex tw-items-center tw-justify-start tw-px-6 tw-py-4'>
            {SECCIONES(t).find((s) => s.id === tabSelected)?.label}
          </div>
          {declaracion347DataIsLoading ? (
            <Spinner />
          ) : (
            <>
              {tabSelected === SECCIONES_TYPES.DECLARANTE && (
                <PlantillaDeclaracionEstandar
                  setValue={setValue}
                  fields={[
                    {
                      id: "row1",
                      inputsOne: [
                        {
                          label: t("taxSummary.declaracion.nif"),
                          code: "nifDeclarante",
                          value: values?.nifDeclarante,
                          inputType: "text",
                          mandatory: true,
                          error: errors?.nifDeclarante?.message as string,
                        },
                        {
                          label: t("taxSummary.declaracion.telephone"),
                          code: "telefonoPersona",
                          value: values?.telefonoPersona,
                          inputType: "text",
                          mandatory: true,
                          error: errors?.telefonoPersona?.message as string,
                        },
                        {
                          label: t("taxSummary.declaracion.name"),
                          code: "nombreDeclarante",
                          value: values?.nombreDeclarante,
                          inputType: "text",
                          colSpan: "tw-col-span-2",
                          mandatory: true,
                          error: errors?.nombreDeclarante?.message as string,
                        },
                      ],
                    },
                    {
                      id: "row2",
                      inputsOne: [
                        {
                          label: t("declaracionModelo347.nifRepresentante"),
                          code: "nifRepresentanteLegal",
                          value: values?.nifRepresentanteLegal,
                          inputType: "text",
                          mandatory: true,
                          error: errors?.nifRepresentanteLegal
                            ?.message as string,
                        },
                        {
                          label: t("taxSummary.declaracion.contact"),
                          code: "apellidosYNombrePersona",
                          value: values?.apellidosYNombrePersona,
                          inputType: "text",
                          mandatory: true,
                          error: errors?.apellidosYNombrePersona
                            ?.message as string,
                        },
                      ],
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.EJERCICIO && (
                <EjercicioDeclaracion ejercicio={values?.ejercicio} />
              )}
              {tabSelected === SECCIONES_TYPES.RESUMEN_DATOS && (
                <PlantillaDeclaracion
                  setValue={setValue}
                  fields={[
                    {
                      label: t("declaracionModelo347.dataSummary.box1"),
                      code: "01",
                      value: values?.["01"],
                      disabled: false,
                      inputType: "numberGroup",
                    },
                    {
                      label: t("declaracionModelo347.dataSummary.box2"),
                      code: "02",
                      inputNumber: "02",
                      value: values?.["02"],
                      disabled: false,
                    },
                    {
                      label: t("declaracionModelo347.dataSummary.box3"),
                      code: "03",
                      value: values?.["03"],
                      disabled: false,
                      inputType: "numberGroup",
                    },
                    {
                      label: t("declaracionModelo347.dataSummary.box4"),
                      code: "04",
                      inputNumber: "04",
                      value: values?.["04"],
                      disabled: false,
                    },
                  ]}
                />
              )}
              {tabSelected === SECCIONES_TYPES.DECLARADOS && (
                <Declarados347
                  setValue={setValue}
                  values={values}
                  errors={errors}
                />
              )}
              {tabSelected === SECCIONES_TYPES.INMUEBLES && (
                <Inmuebles347
                  setValue={setValue}
                  values={values}
                  errors={errors}
                />
              )}{" "}
              <span className='tw-text-xs tw-text-left tw-w-full tw-py-1 tw-px-6'>
                {t("taxSummary.declaracion.infoMsg")}
              </span>
            </>
          )}
        </div>
      </div>

      <ModalPresentarModelo<
        Modelo347FormModel,
        GenerarFicheroModelo347CommandProps
      >
        show={showModalPresentar}
        setShow={setShowModalPresentar}
        nombreDocumento='modelo347.txt'
        postDeclaracion={postDeclaracionModelo347}
        postDeclaracionAdapter={postDeclaracionModelo347Adapter}
        modalTitle='declaracionModelo347.modal.title'
        infoText='taxSummary.declaracion.modal.infoTextTwo'
        values={values}
      />
    </div>
  )
}
export default DeclaracionModelo347
